/**
 * Client-side mirror of the achievement catalogue. The server is the source
 * of truth for which achievements a user has unlocked; this file just
 * provides labels, descriptions, and icons for rendering. Keep ids in sync
 * with server/services/rewards.js.
 */

export const POINT_ACHIEVEMENTS = [
  { id: 'first-step',       label: 'First Step',       points: 20,    icon: '🌱' },
  { id: 'bright-start',     label: 'Bright Start',     points: 50,    icon: '✨' },
  { id: 'page-turner',      label: 'Page Turner',      points: 100,   icon: '📖' },
  { id: 'growing-reader',   label: 'Growing Reader',   points: 200,   icon: '🌿' },
  { id: 'book-explorer',    label: 'Book Explorer',    points: 350,   icon: '🧭' },
  { id: 'story-seeker',     label: 'Story Seeker',     points: 500,   icon: '🔍' },
  { id: 'noor-collector',   label: 'Noor Collector',   points: 750,   icon: '🪔' },
  { id: 'knowledge-seeker', label: 'Knowledge Seeker', points: 1000,  icon: '🧠' },
  { id: 'rising-star',      label: 'Rising Star',      points: 1500,  icon: '⭐' },
  { id: 'golden-reader',    label: 'Golden Reader',    points: 2500,  icon: '🥇' },
  { id: 'master-reader',    label: 'Master Reader',    points: 5000,  icon: '🎓' },
  { id: 'noor-champion',    label: 'Noor Champion',    points: 10000, icon: '🏆' },
];

export const GENRE_ACHIEVEMENTS = {
  history: [
    { id: 'tarikh',       label: 'Tārīkh',             threshold: 1,  icon: '📜' },
    { id: 'ibn-al-athir', label: 'Ibn al-Athir Award', threshold: 3,  icon: '📜' },
    { id: 'ibn-khaldun',  label: 'Ibn Khaldun Award',  threshold: 5,  icon: '📜' },
    { id: 'al-tabari',    label: 'Al-Tabari Award',    threshold: 10, icon: '📜' },
    { id: 'muarrikh',     label: 'Muʾarrikh',          threshold: 20, icon: '📜' },
  ],
  islam: [
    { id: 'bismillah',        label: 'Bismillah',          threshold: 1,  icon: '🕌' },
    { id: 'sunan-abi-dawud',  label: 'Sunan Abi Dawud',    threshold: 3,  icon: '🕌' },
    { id: 'jami-al-tirmidhi', label: 'Jamiʿ al-Tirmidhi',  threshold: 5,  icon: '🕌' },
    { id: 'sahih-muslim',     label: 'Sahih Muslim',       threshold: 10, icon: '🕌' },
    { id: 'sahih-bukhari',    label: 'Sahih Bukhari',      threshold: 20, icon: '🕌' },
  ],
  nonFiction: [
    { id: 'curious-mind', label: 'Curious Mind', threshold: 1,  icon: '🔭' },
    { id: 'wanderer',     label: 'Wanderer',     threshold: 3,  icon: '🔭' },
    { id: 'adventurer',   label: 'Adventurer',   threshold: 5,  icon: '🔭' },
    { id: 'explorer',     label: 'Explorer',     threshold: 10, icon: '🔭' },
    { id: 'researcher',   label: 'Researcher',   threshold: 20, icon: '🔭' },
  ],
  fiction: [
    { id: 'first-tale',     label: 'First Tale',     threshold: 1,  icon: '🎭' },
    { id: 'story-wanderer', label: 'Story Wanderer', threshold: 3,  icon: '🎭' },
    { id: 'chapter-seeker', label: 'Chapter Seeker', threshold: 5,  icon: '🎭' },
    { id: 'tale-keeper',    label: 'Tale Keeper',    threshold: 10, icon: '🎭' },
    { id: 'story-champion', label: 'Story Champion', threshold: 20, icon: '🎭' },
  ],
};

export const SPECIAL_ACHIEVEMENTS = [
  { id: 'first-favourite',  label: 'First Favourite',  icon: '⭐', desc: 'Add your first book to favourites.' },
  { id: 'bilingual-reader', label: 'Bilingual Reader', icon: '🌐', desc: 'Pass the same book’s quiz in English and Arabic.' },
  { id: 'streak-3',         label: 'Streak',           icon: '🔥', desc: 'Pass 3 quizzes in a row first time.' },
];

export const ALL_ACHIEVEMENTS = [
  ...POINT_ACHIEVEMENTS.map((a) => ({ ...a, kind: 'points', desc: `Reach ${a.points.toLocaleString()} Noor Points.` })),
  ...Object.entries(GENRE_ACHIEVEMENTS).flatMap(([genre, list]) =>
    list.map((a) => ({ ...a, kind: 'genre', genre, desc: `Pass ${a.threshold} ${genreLabel(genre)} ${a.threshold === 1 ? 'book' : 'books'}.` }))
  ),
  ...SPECIAL_ACHIEVEMENTS.map((a) => ({ ...a, kind: 'special' })),
];

function genreLabel(genre) {
  if (genre === 'history')    return 'history';
  if (genre === 'islam')      return 'Islamic';
  if (genre === 'nonFiction') return 'non-fiction';
  if (genre === 'fiction')    return 'fiction';
  return genre;
}

export function findAchievement(id) {
  return ALL_ACHIEVEMENTS.find((a) => a.id === id);
}
